import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { usePayment } from '../../context/PaymentContext';
import { useAuth } from '../../context/AuthContext';

/**
 * JobBoostPayment
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - jobId: string
 * - jobTitle?: string
 * - settings: system settings object (expects boost.jobBoostPrice, payments.currency)
 * - onPaid: (transaction) => void
 */
export default function JobBoostPayment({ open, onClose, jobId, jobTitle, settings, onPaid }) {
  const { processJobBoostPayment, commissionRates } = usePayment();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const basePrice = useMemo(() => Number(settings?.boost?.jobBoostPrice || 0), [settings]);
  const currency = useMemo(() => settings?.payments?.currency || 'USD', [settings]);
  const commission = useMemo(() => (basePrice * (commissionRates?.jobPosting || 0)) / 100, [basePrice, commissionRates]);
  const totalAmount = useMemo(() => basePrice + commission, [basePrice, commission]);

  const handleConfirmPayment = async () => {
    try {
      setLoading(true);
      setError('');
      if (!currentUser) {
        setError('Please login to proceed with payment.');
        return;
      }
      const employerId = currentUser?._id || currentUser?.uid;
      const transaction = await processJobBoostPayment(jobId, employerId, basePrice);
      if (transaction) {
        onPaid?.(transaction);
      }
    } catch (e) {
      console.error('Job boost payment failed:', e);
      setError('Failed to process payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Confirm Job Boost</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body1" gutterBottom>
          {jobTitle ? `Job: ${jobTitle}` : `Job ID: ${jobId}`}
        </Typography>
        <Typography variant="body2" gutterBottom>
          Boosting your job will highlight it to seekers and place it among featured listings for the configured duration.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <List>
            <ListItem>
              <ListItemText primary={`Base Price (${currency})`} secondary={basePrice.toFixed(2)} />
            </ListItem>
            <ListItem>
              <ListItemText primary={`Platform Fee (${commissionRates.jobPosting}% )`} secondary={commission.toFixed(2)} />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Total"
                secondary={<Typography variant="h6">{currency} {totalAmount.toFixed(2)}</Typography>}
              />
            </ListItem>
          </List>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" color="primary" onClick={handleConfirmPayment} disabled={loading}>
          {loading ? 'Processing…' : 'Confirm & Pay'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}